/**
 * Edge colouring for the network graph.
 *
 * Colour encodes the signed weight: the ramp runs from the most negative weight,
 * through a neutral midpoint, to the most positive one. Magnitude is carried by
 * stroke width and opacity as well, so hue is never the only cue.
 *
 * Output nodes encode class probability instead. That quantity is bounded and
 * never negative, so it gets a sequential ramp (magma) rather than the switchable
 * diverging one used for weights.
 *
 * Kept free of the DOM and of D3 selections so the encoding is unit-testable; the
 * graph only consumes what this module returns.
 */
import { interpolateMagma, interpolateRdYlGn, interpolateViridis } from 'd3';

export const EDGE_PALETTES = ['red-green', 'viridis'] as const;
export type EdgePalette = (typeof EDGE_PALETTES)[number];

/** Diverging (red → green) versus a viridis ramp read across the signed range. */
const RAMPS: Record<EdgePalette, (t: number) => string> = {
  'red-green': interpolateRdYlGn,
  viridis: interpolateViridis,
};

/**
 * What each palette means, phrased for the graph legend: viridis has no
 * red/green intuition to lean on, so the legend has to name its endpoints.
 */
export const EDGE_PALETTE_DESCRIPTIONS: Record<EdgePalette, string> = {
  'red-green': 'red = negative weight, green = positive weight',
  viridis: 'dark purple = negative weight, yellow = positive weight',
};

/**
 * Endpoints of the input-tile overlay, which shares the edge ramps.
 *
 * Phrased as influence rather than weight: on the tile the value is a neuron's
 * sensitivity to a pixel, not a single connection, so reusing the edge wording
 * would describe the wrong quantity even though the colours match.
 */
export const FOCUS_PALETTE_DESCRIPTIONS: Record<EdgePalette, string> = {
  'red-green': 'red = reduces the neuron\u2019s response, green = increases it',
  viridis: 'dark purple = reduces the neuron\u2019s response, yellow = increases it',
};

/** Output nodes are a fixed colormap; only probability varies, not the palette. */
export const OUTPUT_COLORMAP_DESCRIPTION = 'dark purple = 0% probability, pale yellow = 100%';

/** Evenly spaced colours across a ramp, for legend gradients. */
function sampleRamp(interpolator: (t: number) => string, stops: number): string[] {
  const steps = Math.max(2, stops);
  return Array.from({ length: steps }, (_, index) => interpolator(index / (steps - 1)));
}

export interface EdgeStyle {
  /** Ramp colour at the weight's position on the signed scale. */
  color: string;
  opacity: number;
  width: number;
}

/**
 * Maps a weight onto colour, opacity and stroke width.
 *
 * `scale` is the largest absolute weight in the graph. Normalizing against it keeps
 * the ramp spanning its full range after growth changes the weight distribution.
 * Values beyond `scale` clamp instead of wrapping, so an outlier cannot invert the
 * encoding.
 */
export function edgeStyle(weight: number, scale: number, palette: EdgePalette): EdgeStyle {
  const max = scale > 0 ? scale : 1;
  const ratio = weight / max;
  const magnitude = Math.min(1, Math.abs(ratio));
  // 0 = most negative, 0.5 = zero, 1 = most positive.
  const signed = 0.5 + 0.5 * Math.min(1, Math.max(-1, ratio));
  return {
    color: RAMPS[palette](signed),
    opacity: 0.08 + 0.35 * magnitude,
    width: 0.4 + 1.6 * magnitude,
  };
}

/**
 * Evenly spaced colours across the palette, for the legend gradient.
 *
 * The legend is painted from this function rather than from hand-picked colours,
 * so it cannot drift from what the edges actually draw.
 */
export function paletteRamp(palette: EdgePalette, stops = 11): string[] {
  return sampleRamp(RAMPS[palette], stops);
}

/** Legend gradient for the output-node colormap. */
export function outputRamp(stops = 11): string[] {
  return sampleRamp(interpolateMagma, stops);
}

/** Legend gradient for the input-tile overlay, spanning inhibition through excitation. */
export function focusRamp(palette: EdgePalette, stops = 11): string[] {
  return sampleRamp(RAMPS[palette], stops);
}

export interface FocusStyle {
  color: string;
  opacity: number;
}

/**
 * Weight-independent strength of the pixel overlay.
 *
 * Only the strongest pixel on the tile reaches this opacity; the weaker ones
 * taper toward it through `focusStyle`. Full-strength colour on every pixel that
 * cleared a cut-off would flatten the field into a silhouette, which is the one
 * thing a receptive field must not look like.
 */
const FOCUS_MAX_OPACITY = 0.9;

/**
 * Maps one pixel of a neuron's normalized receptive field onto the overlay.
 *
 * Reuses the edge ramps rather than a ramp of its own: the sign of a pixel's
 * influence is the same quantity the edges encode, so red/green (or dark
 * purple/yellow) means here exactly what it means there, and the existing edge
 * legend already explains it — no third legend row. A sequential ramp such as
 * magma could only carry magnitude, which would make an inhibitory pixel glow
 * exactly like an excitatory one.
 *
 * `sensitivity` is already normalized to the selected neuron's strongest pixel,
 * so the ramp spans its full range regardless of layer depth.
 */
export function focusStyle(sensitivity: number, palette: EdgePalette): FocusStyle {
  const magnitude = Math.min(1, Math.abs(sensitivity));
  if (magnitude === 0) {
    return { color: RAMPS[palette](0.5), opacity: 0 };
  }
  // 0 = strongest inhibition, 0.5 = none, 1 = strongest excitation. The colour is
  // taken at full strength: at 28x28 the hue is most of what the eye can resolve.
  const signed = 0.5 + 0.5 * Math.sign(sensitivity) * magnitude;
  return { color: RAMPS[palette](signed), opacity: magnitude * FOCUS_MAX_OPACITY };
}

/**
 * Maps a class probability onto the output-node fill.
 *
 * Clamped because predictions arrive from the worker and a slightly negative or
 * above-one value would otherwise extrapolate past the ramp's endpoints.
 */
export function outputColor(probability: number): string {
  return interpolateMagma(Math.min(1, Math.max(0, probability)));
}
